import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  entryDailyDetailModalRecoil,
} from '@/atom/ModalAtom';
import { placeListRecoil } from '@/atom/PlaceAtom';
import {
  entryListRecoil,
  entryListLengthRecoil,
  entryCertiIdRecoil,
  entryNameRecoil,
  entryPhoneRecoil,
  entryPlaceIdRecoil,
  entryStartDateRecoil,
  entryEndDateRecoil,
} from '@/atom/EntryAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import { ApiService } from '@/api';
import TitleTextBox from '@/component/TitleTextBox';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import { EntryDto, EntrySearchDto } from '@/model/entry.dto';
import { PlaceDto } from '@/model/place.dto';
import EntryDailyDetailModal from '@/modal/EntryDailyDetailModal';
import ExcelDownloadButton from '@/component/ExcelDownloadButton';

interface TreeNode {
  name: string;
  placeId?: string;
  checked: boolean;
}

const today = new Date();
const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

const Entrymanagement = () => {
  const api = useMemo(() => new ApiService(), []);
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [entryDailyDetailModalState, setEntryDailyDetailModalState] =
    useRecoilState(entryDailyDetailModalRecoil);
  const [entryListState, setEntryListState] = useRecoilState(entryListRecoil);
  const [entryListLengthState, setEntryListLengthState] = useRecoilState(
    entryListLengthRecoil,
  );
  const [entryCertiIdState, setEntryCertiIdState] =
    useRecoilState(entryCertiIdRecoil);
  const [entryNameState, setEntryNameState] = useRecoilState(entryNameRecoil);
  const [entryPhoneState, setEntryPhoneState] =
    useRecoilState(entryPhoneRecoil);
  const [entryPlaceIdState, setEntryPlaceIdState] =
    useRecoilState(entryPlaceIdRecoil);
  const [entryStartDateState, setEntryStartDateState] =
    useRecoilState(entryStartDateRecoil);
  const [entryEndDateState, setEntryEndDateState] =
    useRecoilState(entryEndDateRecoil);
  const [placeListState, setPlaceListState] = useRecoilState(placeListRecoil);

  const [targetDate, setTargetDate] = useState<string>(formattedDate);
  const [startDate, setStartDate] = useState<string>(formattedDate);
  const [endDate, setEndDate] = useState<string>(formattedDate);
  const [name, setName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [certiId, setCertiId] = useState<string>('');
  const [placeIds, setPlaceIds] = useState<string[] | null>(null);
  const [place, setPlace] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [activeSearchOptionTab, setActiveSearchOptionTab] = useState<number>(0);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [treeIsOpen, setTreeIsOpen] = useState(true);
  const [dailyIsOpen, setDailyIsOpen] = useState(true);
  const [periodIsOpen, setPeriodIsOpen] = useState(false);
  const [searchState, setSearchState] = useState<boolean>(false);
  const [firstRender, setFirstRender] = useState<boolean>(false);
  const [entryAllList, setEntryAllList] = useState<EntryDto[]>([]);

  const searchBtn = useRef<HTMLButtonElement>(null);

  const fetchEntryList = async () => {
    if (placeIds !== null) {
      try {
        const dto: EntrySearchDto = {
          name,
          phone,
          certi_id: certiId,
          place_id: placeIds?.join(','),
          start_date: activeSearchOptionTab === 0 ? targetDate : startDate,
          end_date: activeSearchOptionTab === 0 ? targetDate : endDate,
        };
        const response: EntryDto[] = await api.getEntryList(
          String(sessionStorage.getItem('accessToken')),
          dto,
        );
        setEntryAllList(response);
        setEntryListLengthState(response.length);
      } catch (err: any) {
        if (err.response.data.statusCode === 401) {
          sessionStorage.clear();
          navigate('/login');
        }
      }
    }
  };

  const filterEntryList = async () => {
    if (placeIds !== null) {
      const dto: EntrySearchDto = {
        name,
        phone,
        certi_id: certiId,
        place_id: placeIds?.join(','),
        start_date: activeSearchOptionTab === 0 ? targetDate : startDate,
        end_date: activeSearchOptionTab === 0 ? targetDate : endDate,
        offset,
        limit,
      };
      try {
        const response: EntryDto[] = await api.getEntryList(
          String(sessionStorage.getItem('accessToken')),
          dto,
        );
        setEntryListState(response);
      } catch (err: any) {
        if (err.response.data.statusCode === 401) {
          sessionStorage.clear();
          navigate('/login');
        }
      }
    }
  };

  const fetchPlaceList = async () => {
    try {
      const response: PlaceDto[] = await api.getPlaceList(
        String(sessionStorage.getItem('accessToken')),
      );
      const firstPlaceIds: string[] = response.map((place) =>
        String(place.place_id),
      );
      setPlaceIds(firstPlaceIds);
      setPlaceListState(response);
      setFirstRender(true);
    } catch (err: any) {
      if (err.response.data.statusCode === 401) {
        sessionStorage.clear();
        navigate('/login');
      }
    }
  };

  const handleHighlightedStyle = (nodeName: string) => {
    return place && nodeName.includes(place)
      ? { backgroundColor: 'yellow' }
      : {};
  };

  const handleSearchOptionTabClick = (index: number) => {
    setTargetDate(formattedDate);
    setStartDate(formattedDate);
    setEndDate(formattedDate);
    setName('');
    setPhone('');
    setCertiId('');
    setActiveSearchOptionTab(index);
  };

  const handleStartDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value > endDate) {
      setErrorModalText('시작일은 종료일보다 클 수 없습니다.');
      return;
    }
    setStartDate(e.target.value);
    if (searchBtn.current) {
      searchBtn.current.focus();
    }
  };

  const handleEndDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value < startDate) {
      setErrorModalText('종료일은은 시작일보다 작을 수 없습니다.');
      return;
    }
    setEndDate(e.target.value);
    if (searchBtn.current) {
      searchBtn.current.focus();
    }
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setOffset(0);
    setSearchState(!searchState);
  };

  const handleClickSearch = () => {
    setOffset(0);
    setSearchState(!searchState);
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
  };

  const handleCheckChange = (
    checked: boolean,
    index: number,
    placeName: string,
    placeId: string,
  ) => {
    const updatedTree = [...treeData];
    updatedTree[index].checked = checked;
    setTreeData(updatedTree);

    if (checked) {
      setPlaceIds((prev) => (prev ? [...prev, placeId] : [placeId]));
    } else {
      setPlaceIds((prev) =>
        prev ? prev.filter((el) => el !== placeId) : prev,
      );
    }

    if (searchBtn.current) {
      searchBtn.current.focus();
    }
  };

  const handleParentCheckChange = (checked: boolean) => {
    const updatedTree = treeData.map((node) => ({
      ...node,
      checked: checked,
    }));
    setTreeData(updatedTree);

    if (checked) {
      const allPlacePlaceIds = treeData.map((node) => String(node.placeId));
      setPlaceIds(allPlacePlaceIds);
    } else {
      setPlaceIds([]);
    }

    if (searchBtn.current) {
      searchBtn.current.focus();
    }
  };

  const handleDetailModal = (certi_id: string, name: string, phone: string) => {
    setEntryCertiIdState(certi_id);
    setEntryNameState(name);
    setEntryPhoneState(phone);
    if (placeIds) {
      setEntryPlaceIdState(placeIds?.join(','));
    }
    setEntryStartDateState(
      activeSearchOptionTab === 0 ? targetDate : startDate,
    );
    setEntryEndDateState(activeSearchOptionTab === 0 ? targetDate : endDate);
    setEntryDailyDetailModalState(true);
  };

  const toggleOpenState = () => {
    setTreeIsOpen(!treeIsOpen);
  };

  useEffect(() => {
    fetchPlaceList();
  }, []);

  useEffect(() => {
    if (firstRender) {
      fetchEntryList();
      filterEntryList();
      setDailyIsOpen(true);
      setPeriodIsOpen(false);
      setFirstRender(false);
    }
  }, [placeIds]);

  useEffect(() => {
    if (activeSearchOptionTab === 0) {
      fetchEntryList();
      filterEntryList();
      setDailyIsOpen(true);
      setPeriodIsOpen(false);
    } else if (activeSearchOptionTab === 1) {
      fetchEntryList();
      filterEntryList();
      setDailyIsOpen(false);
      setPeriodIsOpen(true);
    }
  }, [limit, offset, searchState]);

  useEffect(() => {
    const tree: TreeNode[] = (placeListState || []).map((place) => ({
      name: place.name,
      placeId: place.place_id,
      checked: true,
    }));

    setTreeData(tree);
  }, [placeListState]);

  return (
    <>
      <Wrap>
        <PlaceOpitonBox>
          <PlaceSearchWrap>
            <PlaceSearchBox>
              <IS.TextInputType1
                type="text"
                placeholder="출입장소"
                value={place}
                onChange={(e) => setPlace(e.target.value)}
              />
            </PlaceSearchBox>
            <PlaceCheckbox>
              <IS.CheckTextBoxType1>
                <button
                  type="button"
                  className={
                    treeIsOpen
                      ? 'open-state img-box'
                      : 'open-state img-box close'
                  }
                  onClick={toggleOpenState}
                >
                  {!treeIsOpen && <img src="./img/ico_point_plus.svg" alt="" />}
                  {treeIsOpen && <img src="./img/ico_point_minus.svg" alt="" />}
                </button>
                <div className="check-box">
                  <input
                    type="checkbox"
                    id="all"
                    checked={
                      treeData.length > 0 &&
                      treeData.every((node) => node.checked)
                    }
                    onChange={(e) => handleParentCheckChange(e.target.checked)}
                  />
                  <label htmlFor="all">
                    <span className="img-box">
                      <img src="./img/ico_check_2.svg" alt="" />
                    </span>
                    <span className="text">출입 장소</span>
                  </label>
                </div>
              </IS.CheckTextBoxType1>
              {treeIsOpen && (
                <ul>
                  {treeData.map((node, index) => (
                    <li key={index}>
                      <IS.CheckTextBoxType1>
                        <div className="check-box">
                          <input
                            type="checkbox"
                            id={`cv-${index}`}
                            checked={node.checked}
                            onChange={(e) =>
                              handleCheckChange(
                                e.target.checked,
                                index,
                                node.name,
                                String(node.placeId),
                              )
                            }
                          />
                          <label htmlFor={`cv-${index}`}>
                            <span className="img-box">
                              <img src="./img/ico_check_2.svg" alt="" />
                            </span>
                            <span
                              className="text text-2"
                              style={handleHighlightedStyle(node.name)}
                            >
                              {node.name}
                            </span>
                          </label>
                        </div>
                      </IS.CheckTextBoxType1>
                    </li>
                  ))}
                </ul>
              )}
            </PlaceCheckbox>
          </PlaceSearchWrap>
          <BS.Btn1Type1
            type="button"
            onClick={handleClickSearch}
            ref={searchBtn}
          >
            적용
          </BS.Btn1Type1>
        </PlaceOpitonBox>
        <CntBox1>
          <TitleTextBox text="출입 관리" />
          <SearchOptionBox>
            <CS.TabMenuType1Mini>
              <button
                type="button"
                className={activeSearchOptionTab === 0 ? 'active' : ''}
                onClick={() => handleSearchOptionTabClick(0)}
              >
                일일 출입관리
              </button>
              <button
                type="button"
                className={activeSearchOptionTab === 1 ? 'active' : ''}
                onClick={() => handleSearchOptionTabClick(1)}
              >
                기간별 출입관리
              </button>
            </CS.TabMenuType1Mini>
            <form onSubmit={handleSearch}>
              <div className="option-box-1">
                {activeSearchOptionTab === 0 && (
                  <InputArea>
                    <IS.TextInputType1
                      style={{ width: 144 }}
                      type="date"
                      value={targetDate}
                      onChange={(e) => {
                        setTargetDate(e.target.value);
                        if (searchBtn.current) {
                          searchBtn.current.focus();
                        }
                      }}
                    />
                    <IS.TextInputType1
                      style={{ width: 144 }}
                      type="text"
                      placeholder="이름"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <IS.TextInputType1
                      style={{ width: 144 }}
                      type="text"
                      placeholder="핸드폰 번호"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                    <IS.TextInputType1
                      style={{ width: 144 }}
                      type="text"
                      placeholder="출입증 번호"
                      value={certiId}
                      onChange={(e) => setCertiId(e.target.value)}
                    />
                  </InputArea>
                )}
                {activeSearchOptionTab === 1 && (
                  <InputArea>
                    <IS.TextInputType1
                      style={{ width: 144 }}
                      type="date"
                      value={startDate}
                      onChange={handleStartDate}
                    />
                    <IS.TextInputType1
                      style={{ width: 144 }}
                      type="date"
                      value={endDate}
                      onChange={handleEndDate}
                    />
                    <IS.TextInputType1
                      style={{ width: 144 }}
                      type="text"
                      placeholder="이름"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <IS.TextInputType1
                      style={{ width: 144 }}
                      type="text"
                      placeholder="핸드폰 번호"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                    <IS.TextInputType1
                      style={{ width: 144 }}
                      type="text"
                      placeholder="출입증 번호"
                      value={certiId}
                      onChange={(e) => setCertiId(e.target.value)}
                    />
                  </InputArea>
                )}
                <SearchBtn />
              </div>
            </form>
          </SearchOptionBox>
          <TableOpitonBox>
            {dailyIsOpen && (
              <h1>
                출입 인원 <span>{entryListLengthState}</span>
              </h1>
            )}
            {periodIsOpen && <h1></h1>}
            <div className="right-box">
              <IS.SelectType1>
                <select
                  value={limit}
                  onChange={handleLimitChange}
                  className="selected"
                  style={{ width: 153 }}
                >
                  <option value={10}>10개씩 보기</option>
                  <option value={20}>20개씩 보기</option>
                  <option value={30}>30개씩 보기</option>
                  <option value={50}>50개씩 보기</option>
                </select>
                <span className="img-box">
                  <img src="/img/arrow_bottom_555.svg" alt="" />
                </span>
              </IS.SelectType1>
              <ExcelDownloadButton
                headers={[
                  { label: '이름', key: 'applicant' },
                  { label: '핸드폰 번호', key: 'phone' },
                  { label: '출입장소', key: 'place_name' },
                  { label: '출입증 번호', key: 'certi_id' },
                  { label: '마지막 출입시간', key: 'entry_time' },
                ]}
                data={entryAllList}
                fileName="출입 관리 목록"
                sheetName="출입 관리 목록"
              />
            </div>
          </TableOpitonBox>
          {dailyIsOpen && (
            <TableBox>
              <table>
                <thead>
                  <tr>
                    <th>No</th>
                    <th>이름​</th>
                    <th>핸드폰 번호​</th>
                    <th>출입장소​</th>
                    <th>출입증 번호​</th>
                    <th>마지막 출입 시간</th>
                  </tr>
                </thead>
                <tbody>
                  {entryListState?.map((entryDaily, index) => (
                    <tr key={entryDaily.id}>
                      <td>{index + 1 + offset}</td>
                      <td>{entryDaily.applicant}</td>
                      <td>{entryDaily.phone}</td>
                      <td>{entryDaily.place_name}</td>
                      <td>{entryDaily.certi_id}</td>
                      <td
                        className="button-text"
                        onClick={() =>
                          handleDetailModal(
                            String(entryDaily.certi_id),
                            String(entryDaily.applicant),
                            String(entryDaily.phone),
                          )
                        }
                      >
                        {entryDaily.entry_time?.split(' ')[1].slice(0, 8)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableBox>
          )}
          {periodIsOpen && (
            <TableBox>
              <table>
                <thead>
                  <tr>
                    <th>No</th>
                    <th>이름​</th>
                    <th>핸드폰 번호​</th>
                    <th>출입장소​</th>
                    <th>출입증 번호​</th>
                    <th>마지막 출입 날짜​</th>
                    <th>마지막 출입 시각</th>
                  </tr>
                </thead>
                <tbody>
                  {entryListState?.map((entryDaily, index) => (
                    <tr key={entryDaily.id}>
                      <td>{index + 1 + offset}</td>
                      <td>{entryDaily.applicant}</td>
                      <td>{entryDaily.phone}</td>
                      <td>{entryDaily.place_name}</td>
                      <td>{entryDaily.certi_id}</td>
                      <td
                        className="button-text"
                        onClick={() =>
                          handleDetailModal(
                            String(entryDaily.certi_id),
                            String(entryDaily.applicant),
                            String(entryDaily.phone),
                          )
                        }
                      >
                        {entryDaily.entry_time?.split(' ')[0]}
                      </td>
                      <td
                        className="button-text"
                        onClick={() =>
                          handleDetailModal(
                            String(entryDaily.certi_id),
                            String(entryDaily.applicant),
                            String(entryDaily.phone),
                          )
                        }
                      >
                        {entryDaily.entry_time?.split(' ')[1].slice(0, 8)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableBox>
          )}
          {entryListState?.length !== 0 && (
            <Pagenation
              listAllLength={entryListLengthState}
              listLimit={limit}
              onCurrentPageChange={handleOffsetChange}
            />
          )}
        </CntBox1>
      </Wrap>
      {entryDailyDetailModalState && <EntryDailyDetailModal />}
    </>
  );
};

export default Entrymanagement;

export const Wrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 20px;
`;

export const PlaceOpitonBox = styled.div`
  width: 260px;
  box-sizing: border-box;
  min-height: calc(100vh - 184px);
  background: #fff;
  border-radius: 12px;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
`;

export const PlaceSearchWrap = styled.div`
  width: 100%;
`;

export const PlaceSearchBox = styled.div`
  display: flex;
  gap: 8px;
  > input {
    width: 100%;
  }
`;

export const PlaceCheckbox = styled.div`
  margin-top: 16px;
  > ul {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    box-sizing: border-box;
    padding-left: 40px;
  }
`;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: calc(100% - 280px);
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
  flex-direction: column;
  gap: 12px;
  .option-box-1 {
    display: flex;
    gap: 20px;
    justify-content: space-between;
    align-items: center;
  }
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 20px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;
